import { Injectable } from '@angular/core';
import { NotificationConfig, NotificationInstance, NotificationType } from '../models/notification.models';
import { NOTIFICATION_STYLES } from '../constants';

/**
 * Notification Service
 * 
 * Provides toast notifications for user feedback throughout the application.
 * Supports multiple notification types (success, error, warning, info) with
 * customizable duration and actions.
 * 
 * @example
 * ```typescript
 * // Success notification
 * notificationService.showSuccess('Login successful!');
 * 
 * // Error notification with custom duration
 * notificationService.showError('Failed to load data', 5000);
 * 
 * // Custom notification with action
 * notificationService.show({
 *   message: 'Settings saved',
 *   type: 'success',
 *   actionText: 'Undo',
 *   action: () => this.undoChanges()
 * });
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private readonly DEFAULT_DURATION = 3000;
    private notifications: NotificationInstance[] = [];
    private nextId = 1;

    /**
     * Show a success notification
     * 
     * @param message - Success message to display
     * @param duration - Duration in milliseconds (default: 3000)
     */
    showSuccess(message: string, duration: number = this.DEFAULT_DURATION): void {
        this.show({
            message,
            type: 'success',
            duration
        });
    }

    /**
     * Show an error notification
     * 
     * @param message - Error message to display
     * @param duration - Duration in milliseconds (default: 5000)
     */
    showError(message: string, duration: number = 5000): void {
        this.show({
            message,
            type: 'error',
            duration
        });
    }

    /**
     * Show a warning notification
     * 
     * @param message - Warning message to display
     * @param duration - Duration in milliseconds (default: 4000)
     */
    showWarning(message: string, duration: number = 4000): void {
        this.show({
            message,
            type: 'warning',
            duration
        });
    }

    /**
     * Show an info notification
     * 
     * @param message - Info message to display
     * @param duration - Duration in milliseconds (default: 3000)
     */
    showInfo(message: string, duration: number = this.DEFAULT_DURATION): void {
        this.show({
            message,
            type: 'info',
            duration
        });
    }

    /**
     * Show a custom notification
     * 
     * @param config - Notification configuration
     */
    show(config: NotificationConfig): void {
        const id = `notification-${this.nextId++}`;
        const notification: NotificationInstance = {
            ...config,
            id,
            timestamp: new Date(),
            duration: config.duration ?? this.DEFAULT_DURATION
        };

        this.notifications.push(notification);
        this.displayNotification(notification);

        // Auto-dismiss if duration is specified
        if (notification.duration && notification.duration > 0) {
            setTimeout(() => {
                this.dismiss(id);
            }, notification.duration);
        }
    }

    /**
     * Dismiss a notification by ID
     * 
     * @param id - Notification ID
     */
    dismiss(id: string): void {
        const index = this.notifications.findIndex(n => n.id === id);
        if (index !== -1) {
            this.notifications.splice(index, 1);
            this.removeNotificationElement(id);
        }
    }

    /**
     * Dismiss all notifications
     */
    dismissAll(): void {
        this.notifications.forEach(n => this.removeNotificationElement(n.id));
        this.notifications = [];
    }

    // ==========================================
    // Pre-defined Messages for Common Scenarios
    // ==========================================

    /**
     * Show login success notification
     */
    showLoginSuccess(username: string): void {
        this.showSuccess(`Welcome back, ${username}!`);
    }

    /**
     * Show logout success notification
     */
    showLogoutSuccess(): void {
        this.showInfo('You have been logged out successfully');
    }

    /**
     * Show auto-authentication success notification
     */
    showAutoAuthSuccess(): void {
        this.showInfo('Session restored automatically');
    }

    /**
     * Show token expiration warning
     */
    showTokenExpiring(minutesRemaining: number): void {
        this.showWarning(
            `Your session will expire in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`,
            0 // Don't auto-dismiss
        );
    }

    /**
     * Show token expired notification
     */
    showTokenExpired(): void {
        this.showError('Your session has expired. Please log in again.');
    }

    /**
     * Show unauthorized access notification
     */
    showUnauthorized(): void {
        this.showError('You are not authorized to access this resource.');
    }

    /**
     * Show network error notification
     */
    showNetworkError(): void {
        this.showError('Network error. Please check your connection and try again.');
    }

    /**
     * Display notification in the UI
     * 
     * This method creates and displays a toast notification element.
     * Uses modern CSS and animations for a polished UX.
     */
    private displayNotification(notification: NotificationInstance): void {
        // Create container if it doesn't exist
        let container = document.getElementById('swagger-auth-notifications');
        if (!container) {
            container = document.createElement('div');
            container.id = 'swagger-auth-notifications';
            container.className = 'swagger-auth-notification-container';
            document.body.appendChild(container);
        }

        // Create notification element
        const element = document.createElement('div');
        element.id = notification.id;
        element.className = `swagger-auth-notification swagger-auth-notification-${notification.type}`;

        // Add icon based on type
        const icon = this.getIconForType(notification.type);

        // Build notification HTML
        let html = `
            <div class="swagger-auth-notification-icon">${icon}</div>
            <div class="swagger-auth-notification-content">
                ${notification.title ? `<div class="swagger-auth-notification-title">${notification.title}</div>` : ''}
                <div class="swagger-auth-notification-message">${notification.message}</div>
            </div>
        `;

        // Add action button if provided
        if (notification.actionText && notification.action) {
            html += `
                <button class="swagger-auth-notification-action" data-action="true">
                    ${notification.actionText}
                </button>
            `;
        }

        // Add close button
        html += `
            <button class="swagger-auth-notification-close" data-close="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 10.1l-1.4 1.4L8 9.4l-2.1 2.1-1.4-1.4L6.6 8 4.5 5.9l1.4-1.4L8 6.6l2.1-2.1 1.4 1.4L9.4 8l2.1 2.1z"/>
                </svg>
            </button>
        `;

        element.innerHTML = html;

        // Add event listeners
        const closeButton = element.querySelector('[data-close="true"]');
        closeButton?.addEventListener('click', () => this.dismiss(notification.id));

        const actionButton = element.querySelector('[data-action="true"]');
        if (actionButton && notification.action) {
            actionButton.addEventListener('click', () => {
                notification.action!();
                this.dismiss(notification.id);
            });
        }

        // Add to container with animation
        container.appendChild(element);

        // Trigger animation
        setTimeout(() => element.classList.add('swagger-auth-notification-show'), 10);
    }

    /**
     * Remove notification element from DOM
     */
    private removeNotificationElement(id: string): void {
        const element = document.getElementById(id);
        if (element) {
            element.classList.remove('swagger-auth-notification-show');
            element.classList.add('swagger-auth-notification-hide');

            // Remove after animation completes
            setTimeout(() => {
                element.remove();

                // Remove container if empty
                const container = document.getElementById('swagger-auth-notifications');
                if (container && container.children.length === 0) {
                    container.remove();
                }
            }, 300);
        }
    }

    /**
     * Get icon SVG for notification type
     */
    private getIconForType(type: NotificationType): string {
        const icons: Record<NotificationType, string> = {
            success: `
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 0C4.5 0 0 4.5 0 10s4.5 10 10 10 10-4.5 10-10S15.5 0 10 0zm-2 15l-5-5 1.4-1.4L8 12.2l7.6-7.6L17 6l-9 9z"/>
                </svg>
            `,
            error: `
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 0C4.5 0 0 4.5 0 10s4.5 10 10 10 10-4.5 10-10S15.5 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z"/>
                </svg>
            `,
            warning: `
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M1 17h18L10 0 1 17zm10-2H9v-2h2v2zm0-4H9V7h2v4z"/>
                </svg>
            `,
            info: `
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 0C4.5 0 0 4.5 0 10s4.5 10 10 10 10-4.5 10-10S15.5 0 10 0zm1 15H9V9h2v6zm0-8H9V5h2v2z"/>
                </svg>
            `
        };

        return icons[type];
    }
}

// Inject styles on first import
if (typeof document !== 'undefined') {
    const styleId = 'swagger-auth-notification-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = NOTIFICATION_STYLES;
        document.head.appendChild(style);
    }
}
